import { useTranslation } from '@dreamstack/i18n'
import { SimpleInput } from '@dreamstack/simple-components/src/lib/SimpleInput'
import { generateIdForText } from '@dreamstack/util'
import Image from 'next/image'
import type { FunctionComponent } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { FormCheckbox } from '../forms/FormCheckbox'
import { IS_ACCENTRO_INVESTORS, IS_ACCENTRO_WEB } from '../config/config'
import { useNotificationContext } from '../notification/NotificationContext'
import { useIsMdOrGreater } from '../hooks/useWindowSize'
import { SimpleMuiButton } from '../layout/SimpleMuiButton'
import { HeadingH2 } from '../layout/Texts'
import type { contactFormType } from './typings'
import { useContactFormSchema } from './useContactFormSchema'
import { useSubmitContactForm } from './useSubmitContactForm'

const IS_INTERNATIONAL = !IS_ACCENTRO_WEB && !IS_ACCENTRO_INVESTORS

export type ContactFormProps = {
  type?: contactFormType
}

export const ContactForm: FunctionComponent<
  React.PropsWithChildren<ContactFormProps>
> = ({ type }) => {
  const t = useTranslation()
  const isMd = useIsMdOrGreater()

  const yupSchemaResolver = useContactFormSchema()
  const methods = useForm({ resolver: yupSchemaResolver })
  const { handleSubmit, control, reset } = methods

  const onSuccess = () => {
    addNotification('success', t('accentro:contactform.successMessage'))
    reset()
  }
  const { submitFn: onSubmit, loading } = useSubmitContactForm({
    successCallback: onSuccess,
    formId: 'contact',
    type: type ?? 'contactform',
  })

  const { addNotification } = useNotificationContext()
  const title = t('accentro:contactform.contactform')
  return (
    <div>
      <HeadingH2 id={generateIdForText(title)} className="mb-px-48 md:mb-px-64">
        {title}
      </HeadingH2>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 gap-px-24 md:grid-cols-2">
            {!IS_INTERNATIONAL && (
              <Controller
                defaultValue=""
                name="clientSalutation"
                control={control}
                render={({ field }) => (
                  <SimpleInput
                    {...field}
                    label={t('accentro:contactform.salutation')}
                    fullWidth
                  />
                )}
              />
            )}
            <div className="col-start-1">
              <Controller
                defaultValue=""
                name="clientFirstName"
                control={control}
                render={({ field }) => (
                  <SimpleInput
                    {...field}
                    label={t('accentro:contactform.firstname')}
                    fullWidth
                  />
                )}
              />
            </div>
            <Controller
              defaultValue=""
              name="clientLastName"
              control={control}
              render={({ field }) => (
                <SimpleInput
                  {...field}
                  label={t('accentro:contactform.lastname')}
                  fullWidth
                />
              )}
            />
            {!IS_INTERNATIONAL && (
              <>
                <Controller
                  defaultValue=""
                  name="clientStreet"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={t('accentro:contactform.street')}
                      fullWidth
                    />
                  )}
                />
                <Controller
                  defaultValue=""
                  name="clientStreetnumber"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={t('accentro:contactform.streetnumber')}
                      fullWidth
                    />
                  )}
                />
                <Controller
                  defaultValue=""
                  name="clientZipCode"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={t('accentro:contactform.zipcode')}
                      fullWidth
                    />
                  )}
                />
                <Controller
                  defaultValue=""
                  name="clientCity"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={t('accentro:contactform.city')}
                      fullWidth
                    />
                  )}
                />
              </>
            )}
            <Controller
              defaultValue=""
              name="clientEmail"
              control={control}
              render={({ field }) => (
                <SimpleInput
                  {...field}
                  label={`${t('accentro:contactform.email')}*`}
                  fullWidth
                />
              )}
            />
            <Controller
              defaultValue=""
              name="clientPhone"
              control={control}
              render={({ field }) => (
                <SimpleInput
                  {...field}
                  label={`${t('accentro:contactform.phone')}*`}
                  fullWidth
                />
              )}
            />
            {!IS_INTERNATIONAL && (
              <div className="col-span-full">
                <Controller
                  defaultValue=""
                  name="subject"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={t('accentro:contactform.subject')}
                      fullWidth
                    />
                  )}
                />
              </div>
            )}
            {IS_INTERNATIONAL && (
              <div className="col-span-full">
                <Controller
                  defaultValue=""
                  name="country"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={t('accentro:contactform.country')}
                      fullWidth
                    />
                  )}
                />
              </div>
            )}
            <div className="col-span-full">
              <Controller
                defaultValue=""
                name="text"
                control={control}
                render={({ field }) => (
                  <SimpleInput
                    {...field}
                    label={`${t('accentro:contactform.message')}*`}
                    multiline
                    minRows={4}
                    fullWidth
                  />
                )}
              />
            </div>

            {IS_INTERNATIONAL && (
              <div className="col-span-full bg-accentroAquaVeryLight-full flex flex-row text-px-20 px-px-64 py-px-48">
                <div className="flex-1 pr-px-64">
                  <div className="text-px-28 font-semibold mb-px-24">
                    There is more for you!
                  </div>

                  <div>
                    With the 14. ACCENTRO Homeownership Report 2021 you get the
                    best real estate insights at a glance.
                  </div>

                  <div className="mt-px-24">
                    <FormCheckbox
                      propertyName="subscribeHomeOwnerShipReport"
                      labelComponent={`${t(
                        'accentro:contactform.homeOwnershipReportConsentText'
                      )}`}
                    />
                  </div>
                </div>
                {isMd && (
                  <div className="flex-1 relative">
                    <Image
                      src={'/static/images/WER-en.png'}
                      width={439}
                      height={346}
                      alt={'WER Report image'}
                    />
                  </div>
                )}
              </div>
            )}

            <div className="col-span-full">
              <FormCheckbox
                propertyName="consent"
                labelComponent={`${t('accentro:contactform.consentText')}*`}
              />
            </div>
            {IS_ACCENTRO_WEB && (
              <div className="col-span-full">
                <FormCheckbox
                  className="flex self-start"
                  propertyName="subscribeNewsletter"
                  labelComponent={`${t(
                    'accentro:contactform.consentNewsletterText'
                  )}`}
                />
              </div>
            )}
            <div className="col-span-full text-accentroGray-500">
              <p>{t('accentro:contactform.requiredFieldsText')}</p>
            </div>
            <SimpleMuiButton
              variant="contained"
              type="submit"
              color="primary"
              disabled={loading}
              className="w-60"
              loading={loading}
            >
              {t('accentro:contactform.sendMessage')}
            </SimpleMuiButton>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
